// botAPI.js - HTTP клиент для работы с Bot Backend API

import { config } from './config.js';
import { Platform } from './utils/platform';
import { logger } from './utils/logger.js';
import AuthManager from './utils/authManager';

class BotAPI {
  constructor() {
    this.baseURL = config.BOT_API_URL;
    this.timeout = config.REQUEST_TIMEOUT;
  }

  // Базовый метод для HTTP запросов
  async request(endpoint, options = {}) {
    // Если baseURL начинается с /api, то это относительный путь
    const url = this.baseURL.startsWith('/') 
      ? `${this.baseURL}${endpoint}`.replace('/api/api', '/api')
      : `${this.baseURL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'X-Client-Version': config.APP_VERSION,
    };

    // Добавляем данные пользователя через Platform API
    const user = Platform.getUser();
    if (user && user.id) {
      defaultHeaders['X-Platform-User-ID'] = user.id.toString();
      defaultHeaders['X-Platform-Type'] = Platform.getPlatformType();
      defaultHeaders['X-Platform-Username'] = user.username || '';
    }

    // Добавляем JWT token если есть
    const token = Platform.getAuthToken();
    if (token) {
      defaultHeaders['Authorization'] = 'Bearer ' + token;
    }

    const requestOptions = {
      method: options.method || 'GET',
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      ...options,
    };

    if (options.body && typeof options.body === 'object') {
      requestOptions.body = JSON.stringify(options.body);
    }

    try {
      logger.log(`📡 API запрос: ${requestOptions.method} ${endpoint}`);
      logger.log(`📡 URL: ${url}`);
      if (requestOptions.body) {
        logger.log(`📡 Тело запроса:`, JSON.parse(requestOptions.body));
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);
      
      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Auto-refresh on 401: try to refresh token and retry once
        if (response.status === 401 && !options._retried) {
          const newToken = await AuthManager.refreshAccessToken();
          if (newToken) {
            Platform.saveAuthToken(newToken);
            return this.request(endpoint, { ...options, _retried: true });
          }
        }

        // Пытаемся получить детали ошибки из ответа
        let errorDetail = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = await response.json();
          logger.log(`API error: ${endpoint}`, { status: response.status, data: errorData });
          
          if (errorData.detail) {
            // FastAPI 422 returns detail as array of validation errors
            if (Array.isArray(errorData.detail)) {
              errorDetail = errorData.detail.map(e => e.msg || JSON.stringify(e)).join('; ');
            } else {
              errorDetail = errorData.detail;
            }
          } else if (errorData.message) {
            errorDetail = errorData.message;
          } else if (errorData.error) {
            errorDetail = errorData.error;
          } else {
            // Если структура ошибки неизвестна, выводим всё
            errorDetail = JSON.stringify(errorData);
          }
        } catch (e) {
          // Если не удалось распарсить JSON, используем стандартное сообщение
          logger.log('Failed to parse error response:', e);
        }
        
        const error = new Error(errorDetail);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      logger.log(`✅ API ответ: ${endpoint}`, data);
      
      return data;

    } catch (error) {
      logger.log(`API error: ${endpoint}`, error);

      if (error.name === 'AbortError') {
        throw new Error('Превышено время ожидания запроса');
      }
      
      throw error;
    }
  }

  // GET запрос
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST запрос
  async post(endpoint, data = null) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
    });
  }

  // PUT запрос
  async put(endpoint, data = null) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data,
    });
  }

  // DELETE запрос
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // === API МЕТОДЫ ===

  // Проверка работоспособности API
  async healthCheck() {
    return this.get('/api/health');
  }

  // Получить каталог товаров из глобального цикла
  async getProducts() {
    // Глобальный цикл - district_id не требуется
    return this.get('/api/products');
  }

  // Получить сториз (Instagram-style)
  async getStories() {
    return this.get('/api/stories');
  }

  // Получить категории товаров
  async getCategories() {
    return this.get('/api/categories');
  }

  // Получить данные глобального цикла (статус, даты, тема)
  async getGlobalCycle() {
    return this.get('/api/cycles/global');
  }

  // Получить районы доставки
  async getDistricts() {
    // Возвращаем только районы верхнего уровня (23 шт: 9 АО + 14 МО)
    // 117 подрайонов Москвы используются только для DaData определения адреса
    return this.get('/api/districts?top_level_only=true');
  }

  // Создать заказ
  async createOrder(orderData) {
    // Дополняем данные информацией о пользователе
    const user = Platform.getUser();
    const enrichedOrderData = {
      ...orderData,
      telegram_user_id: user?.telegram_id || user?.id || null,
      username: user?.username || null,
      full_name: user ? `${user.firstName || user.first_name || ''}${(user.lastName || user.last_name) ? ' ' + (user.lastName || user.last_name) : ''}`.trim() : null,
      language_code: user?.languageCode || 'ru',
      created_at: new Date().toISOString(),
    };

    return this.post('/api/orders', enrichedOrderData);
  }

  // Отправить контактные данные
  async sendContact(contactData) {
    const user = Platform.getUser();
    const enrichedContactData = {
      ...contactData,
      telegram_user_id: user?.telegram_id || user?.id || null,
      username: user?.username || null,
      full_name: user ? `${user.firstName || user.first_name || ''}${(user.lastName || user.last_name) ? ' ' + (user.lastName || user.last_name) : ''}`.trim() : null,
      sent_at: new Date().toISOString(),
    };

    return this.post('/api/contacts', enrichedContactData);
  }

  // Получить историю заказов пользователя
  async getUserOrders() {
    const userId = Platform.getUser()?.id;
    if (!userId) {
      throw new Error('ID пользователя не найден');
    }
    return this.get(`/api/orders/user/${userId}`);
  }

  // Получить активные совместные покупки пользователя
  async getMyPackShareInterests() {
    return this.get('/api/pack-share/my-interests');
  }

  // Применить реферальный код
  async applyReferral(referralCode, source = 'web', sourceProductId = null) {
    return this.post('/api/referral/apply', {
      referral_code: referralCode,
      source,
      source_product_id: sourceProductId,
    });
  }

  // Совместная покупка: присоединиться
  async packShareJoin(cycleProductId, desiredQuantity) {
    return this.post(`/api/pack-share/${cycleProductId}/interest`, { desired_quantity: desiredQuantity });
  }

  // Совместная покупка: прогресс
  async packShareProgress(cycleProductId) {
    return this.get(`/api/pack-share/${cycleProductId}/progress`);
  }

  // Совместная покупка: отменить
  async packShareCancel(cycleProductId) {
    return this.delete(`/api/pack-share/${cycleProductId}/interest`);
  }

  // Получить детали заказа
  async getOrderDetail(orderId) {
    return this.get(`/api/orders/${orderId}`);
  }

  // Изменить количество товара в заказе
  async updateOrderItemQuantity(orderId, itemId, quantity) {
    return this.put(`/api/orders/${orderId}/items/${itemId}`, { quantity });
  }

  // Удалить товар из заказа
  async deleteOrderItem(orderId, itemId) {
    return this.delete(`/api/orders/${orderId}/items/${itemId}`);
  }

  // Добавить товар в заказ
  async addOrderItem(orderId, productId, quantity) {
    return this.post(`/api/orders/${orderId}/items`, { product_id: productId, quantity });
  }

  // Удалить заказ целиком
  async deleteOrder(orderId) {
    return this.delete(`/api/orders/${orderId}`);
  }

  // Получить доступные товары для добавления в заказ
  async getAvailableProducts(orderId) {
    return this.get(`/api/orders/${orderId}/available-products`);
  }

  // Проверить промокод
  async checkPromoCode(code) {
    return this.post('/api/promo/check', { code });
  }

  // === ADMIN МЕТОДЫ (если пользователь админ) ===

  // Получить все заказы за сегодня
  async getTodayOrders() {
    return this.get('/api/admin/orders/today');
  }

  // Получить статистику
  async getStats() {
    return this.get('/api/admin/stats');
  }

  // === CART SNAPSHOT МЕТОДЫ ===

  /**
   * Сохранить snapshot корзины для аналитики и уведомлений
   *
   * Для глобального цикла district_id не требуется (определяется по адресу при оформлении заказа)
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @param {number|null} cycleId - ID цикла
   * @param {Array} cartItems - Массив товаров в корзине
   * @returns {Promise} Результат сохранения
   */
  async saveCartSnapshot(telegramId, cycleId, cartItems) {
    try {
      // Преобразуем товары в нужный формат
      const formattedItems = cartItems.map(item => ({
        product_id: parseInt(item.product_id || item.id),
        quantity: parseInt(item.quantity),
        price: parseFloat(item.price || item.current_price || item.basePricePerKg),
        name: item.product_name || item.name,
        image_url: item.image_url || item.imageUrl || null
      }));

      return this.post('/api/cart-snapshot', {
        telegram_id: telegramId,
        district_id: null,  // Глобальный цикл - район определится по адресу доставки
        cycle_id: cycleId,
        cart_items: formattedItems
      });
    } catch (error) {
      console.warn('⚠️ Не удалось сохранить snapshot корзины:', error);
      // Не бросаем ошибку - это фоновая операция
      return { success: false, error: error.message };
    }
  }

  /**
   * Получить последний snapshot корзины пользователя
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @param {number|null} districtId - ID района (опционально)
   * @returns {Promise} Snapshot корзины
   */
  async getCartSnapshot(telegramId, districtId = null) {
    try {
      const params = districtId ? `?district_id=${districtId}` : '';
      return this.get(`/api/cart-snapshot/${telegramId}${params}`);
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить snapshot корзины:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Получить баланс пользователя
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @param {boolean} includeTransactions - Включить транзакции в ответ
   * @param {number} transactionsLimit - Лимит транзакций
   * @returns {Promise} Данные баланса
   */
  async getUserBalance(telegramId, includeTransactions = true, transactionsLimit = 20) {
    try {
      const params = new URLSearchParams({
        include_transactions: includeTransactions,
        transactions_limit: transactionsLimit
      });
      return this.get(`/api/user/${telegramId}/balance?${params}`);
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить баланс:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Получить реферальную информацию пользователя
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @returns {Promise} Реферальные данные
   */
  async getUserReferral(telegramId) {
    try {
      return this.get(`/api/user/${telegramId}/referral`);
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить реферальные данные:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Получить полный профиль пользователя (личный кабинет)
   *
   * Комбинированный endpoint, возвращающий:
   * - Бонусы (текущий баланс, заработано, потрачено, сэкономлено)
   * - История операций (последние 10 транзакций)
   * - Информация об организаторстве (статус заявки)
   * - Приглашения (только для организаторов)
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @returns {Promise} Полные данные профиля
   */
  async getUserProfile(telegramId) {
    try {
      return this.get(`/api/profile/${telegramId}`);
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить профиль:', error);
      // Сохраняем HTTP-статус, чтобы UI отличал 401 (сессия истекла) от прочих сбоев
      return { success: false, error: error.message, status: error.status };
    }
  }

  /**
   * Update user contact information
   * @param {number} telegramId - Telegram ID
   * @param {Object} contactData - Contact fields to update
   * @returns {Promise} Updated contact data
   */
  async updateProfile(telegramId, contactData) {
    return this.put(`/api/profile/${telegramId}/contact`, contactData);
  }

  /**
   * Получить полную историю транзакций пользователя
   *
   * Используется для модалки "Вся история" в ProfilePage.
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @param {number} limit - Максимальное количество транзакций (по умолчанию 50, макс 100)
   * @returns {Promise} Список транзакций
   */
  async getUserTransactions(telegramId, limit = 50) {
    try {
      const params = new URLSearchParams({ limit });
      return this.get(`/api/profile/${telegramId}/transactions?${params}`);
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить историю транзакций:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Сделать пользователя организатором
   *
   * Упрощённый поток: сразу даёт статус организатора без формы.
   *
   * @param {number} telegramId - Telegram ID пользователя
   * @returns {Promise} Результат операции с обновлённым профилем
   */
  async becomeOrganizer(telegramId) {
    try {
      return this.post(`/api/user/${telegramId}/become-organizer`);
    } catch (error) {
      console.warn('⚠️ Не удалось активировать статус организатора:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Получить участников товара (кто уже заказал)
   *
   * @param {number} productId - ID товара
   * @param {number|null} districtId - ID района (опционально)
   * @param {number} limit - Максимальное количество участников
   * @returns {Promise} Список участников
   */
  async getProductParticipants(productId, districtId = null, limit = 10) {
    try {
      const params = new URLSearchParams({ limit });
      if (districtId) params.append('district_id', districtId);
      return this.get(`/api/products/${productId}/participants?${params}`);
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить участников:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Инициализация авторизации (JWT)
   * Отправляет platform и initData на сервер, получает JWT token
   *
   * @param {string} platform - Тип платформы ('telegram' | 'max' | 'web')
   * @param {string} initData - Raw initData строка для верификации
   * @returns {Promise} { success, token, user }
   */
  async authInit(platform, initData) {
    return this.post('/api/auth/init', { platform, init_data: initData });
  }

  // === PAYMENT МЕТОДЫ ===

  /**
   * Создать платёж для заказа через ЮKassa
   *
   * @param {number} orderId - ID заказа
   * @param {string} returnUrl - URL для возврата после оплаты
   * @returns {Promise} { success, payment_id, confirmation_url }
   */
  async createPayment(orderId, returnUrl) {
    return this.post('/api/payments/create', {
      order_id: orderId,
      amount: 0, // сервер посчитает сам по заказу
      description: `Заказ №${orderId}`,
      return_url: returnUrl,
    });
  }

  /**
   * Получить статус платежа
   *
   * @param {string} paymentId - ID платежа (ЮKassa)
   * @returns {Promise} { success, status, paid, amount }
   */
  async getPaymentStatus(paymentId) {
    return this.get(`/api/payments/${paymentId}/status`);
  }

  /**
   * Отправить согласие на обработку персональных данных
   *
   * @param {number} userId - ID пользователя
   * @returns {Promise} Результат
   */
  async sendPrivacyConsent(userId) {
    return this.post(`/api/user/${userId}/privacy-consent`);
  }

  /**
   * Отправить согласие на использование cookie (аудит-лог)
   *
   * @param {string} action - Действие ('granted' | 'revoked')
   * @returns {Promise} Результат
   */
  async sendCookieConsent(action = 'granted') {
    return this.post('/api/consent/cookies', { action });
  }
}

// Создаем единый экземпляр API клиента
const botAPI = new BotAPI();

// === УТИЛИТЫ ===

export const APIUtils = {
  // Проверка доступности API
  async checkConnection() {
    try {
      await botAPI.healthCheck();
      return true;
    } catch (error) {
      logger.error('API unavailable:', error);
      return false;
    }
  },

  // Диагностика API
  async diagnose() {
    logger.log('🔍 Диагностика API соединения...');

    const results = {
      baseURL: botAPI.baseURL,
      isConnected: false,
      platform: Platform.getPlatformType(),
      telegram: Platform.isTelegram(),
      user: Platform.getUser(),
      errors: []
    };

    try {
      // Проверяем health endpoint
      const health = await botAPI.healthCheck();
      results.isConnected = true;
      results.health = health;
      logger.log('✅ API работает');
    } catch (error) {
      results.errors.push(`Health check: ${error.message}`);
      logger.log('❌ API недоступен');
    }

    try {
      // Проверяем загрузку продуктов
      const products = await botAPI.getProducts();
      results.productsCount = products?.products?.length || 0;
      logger.log(`✅ Продуктов загружено: ${results.productsCount}`);
    } catch (error) {
      results.errors.push(`Products: ${error.message}`);
    }

    try {
      // Проверяем загрузку категорий
      const categories = await botAPI.getCategories();
      results.categoriesCount = categories?.categories?.length || 0;
      logger.log(`✅ Категорий загружено: ${results.categoriesCount}`);
    } catch (error) {
      results.errors.push(`Categories: ${error.message}`);
    }

    logger.log('📋 Результаты диагностики:', results);
    return results;
  },

  // Форматирование ошибок для пользователя
  formatError(error) {
    if (error.message.includes('Failed to fetch')) {
      return 'Не удается подключиться к серверу. Проверьте интернет-соединение.';
    }
    if (error.message.includes('timeout')) {
      return 'Превышено время ожидания. Попробуйте позже.';
    }
    if (error.message.includes('HTTP 5')) {
      return 'Ошибка сервера. Мы уже работаем над решением.';
    }
    if (error.message.includes('HTTP 4')) {
      return 'Неверный запрос. Обновите страницу и попробуйте снова.';
    }
    
    return error.message || 'Произошла неизвестная ошибка';
  }
};

export default botAPI;